import React, { useCallback, useEffect, useState } from "react";
import { Globe, Images, Loader2 } from "lucide-react";
import { getServicePages, getGallerySettings, updateGallerySettings } from "../../api";
import { useToast } from "../../hooks/use-toast";
import ServicePageEditor from "./site/ServicePageEditor";
import LegalEditor from "./site/LegalEditor";
import SimulatorEditor from "./site/SimulatorEditor";
import { log } from "../../lib/log";

/** Onglet « Site & services » : pages services éditables (IA) + réglage de la galerie de réalisations. */
const SitePanel = ({ isMaster }) => {
  const [pages, setPages] = useState(null);
  const [aiOk, setAiOk] = useState(true);
  const [current, setCurrent] = useState("");
  const [showDemo, setShowDemo] = useState(true);
  const { toast } = useToast();

  const load = useCallback(async () => {
    try {
      const r = await getServicePages();
      setPages(r.data.items || []); setAiOk(r.data.ai_ok);
      setCurrent((c) => c || r.data.items?.[0]?.slug || "");
      const g = await getGallerySettings(); setShowDemo(g.data.show_demo);
    } catch (e) { log.error("site panel:", e); toast({ title: "Pages indisponibles", variant: "destructive" }); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const toggleDemo = async (v) => {
    try { const r = await updateGallerySettings(v); setShowDemo(r.data.show_demo); toast({ title: v ? "Visuels de démonstration affichés" : "Visuels de démonstration masqués" }); }
    catch (e) { toast({ title: "Erreur", description: e?.response?.data?.detail || "Échec", variant: "destructive" }); }
  };

  const page = pages?.find((p) => p.slug === current);
  return (
    <div data-testid="site-panel">
      <div className="flex items-center gap-2 mb-2"><Globe size={20} className="text-brand" /><h2 className="font-heading font-bold text-xl text-neutral-900">Site & pages services</h2></div>
      <p className="text-sm text-neutral-500 mb-5">Chaque service dispose d'une page détaillée sur le site (menu « Services »). Modifiez les textes ou demandez à l'IA une nouvelle version : la proposition n'est publiée qu'après « Enregistrer ».</p>

      <div className="bg-white border border-neutral-200 rounded-xl p-4 mb-5 flex flex-wrap items-center gap-4" data-testid="gallery-settings">
        <Images size={18} className="text-brand" />
        <div className="flex-1 min-w-[220px]">
          <p className="font-semibold text-neutral-900 text-sm">Galerie « Réalisations » (<a href="/realisations" target="_blank" rel="noreferrer" className="text-brand hover:underline">/realisations</a>)</p>
          <p className="text-xs text-neutral-500">Publiez une intervention terminée depuis l'onglet Interventions (« Publier dans la galerie »). Les visuels de démonstration complètent la galerie tant que vous avez peu de photos.</p>
        </div>
        <label className={`text-sm flex items-center gap-2 ${isMaster ? "" : "opacity-60"}`}><input type="checkbox" className="accent-brand w-4 h-4" checked={showDemo} disabled={!isMaster} onChange={(e) => toggleDemo(e.target.checked)} data-testid="gallery-demo-toggle" /> Afficher les visuels de démonstration</label>
      </div>

      <LegalEditor isMaster={isMaster} />
      <SimulatorEditor isMaster={isMaster} />

      {!pages ? <p className="text-sm text-neutral-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Chargement…</p> : (<>
        <div className="flex flex-wrap gap-2 mb-4" data-testid="service-tabs">
          {pages.map((p) => (
            <button key={p.slug} onClick={() => setCurrent(p.slug)} data-testid={`service-tab-${p.slug}`}
              className={`text-sm font-semibold px-3 py-1.5 rounded-full border ${current === p.slug ? "bg-brand text-white border-brand" : "border-neutral-300 text-neutral-700 hover:border-brand"} ${p.published === false ? "opacity-60 line-through" : ""}`}>{p.title}</button>
          ))}
        </div>
        {page && <ServicePageEditor key={page.slug + (page.updated_at || "")} page={page} aiOk={aiOk} onSaved={(d) => setPages((l) => l.map((x) => (x.slug === d.slug ? d : x)))} />}
      </>)}
    </div>
  );
};

export default SitePanel;
